/** A point in CSS pixels. */
export interface CssPoint {
  x: number;
  y: number;
}

/** The usable, scrollbar-free viewport inside a scroll container. */
export interface ScrollportRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** A clamped scroll position in CSS pixels. */
export interface ScrollPosition {
  left: number;
  top: number;
}

/**
 * Measures the usable scrollport rather than the element's border box.
 *
 * `getBoundingClientRect()` includes classic scrollbar tracks while
 * `clientWidth` and `clientHeight` do not. Camera calculations must use the
 * latter so a layout-consuming scrollbar and an overlay scrollbar describe
 * the same viewport contract.
 */
export function measureScrollport(container: HTMLElement): ScrollportRect {
  const bounds = container.getBoundingClientRect();
  return {
    left: bounds.left + container.clientLeft,
    top: bounds.top + container.clientTop,
    width: container.clientWidth,
    height: container.clientHeight,
  };
}

/** Returns the screen-space center of the usable scrollport. */
export function scrollportCenter(container: HTMLElement): CssPoint {
  const scrollport = measureScrollport(container);
  return {
    x: scrollport.left + scrollport.width / 2,
    y: scrollport.top + scrollport.height / 2,
  };
}

/**
 * Calculates the scroll offset that keeps a canvas point at a requested
 * screen coordinate after a layout or scale change.
 *
 * The browser may change `clientWidth` or `clientHeight` when classic
 * scrollbars appear, including the cross-axis case where one scrollbar makes
 * the other necessary. Reading the final scroll limits here makes the
 * compensation independent of any assumed scrollbar thickness.
 */
export function anchoredScrollPosition(
  container: HTMLElement,
  canvas: HTMLElement,
  canvasPoint: CssPoint,
  targetScreenPoint: CssPoint,
  pointScale = 1
): ScrollPosition {
  const canvasRect = canvas.getBoundingClientRect();
  const desiredLeft = container.scrollLeft + canvasRect.left + canvasPoint.x * pointScale - targetScreenPoint.x;
  const desiredTop = container.scrollTop + canvasRect.top + canvasPoint.y * pointScale - targetScreenPoint.y;
  const maxLeft = Math.max(0, container.scrollWidth - container.clientWidth);
  const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);

  return {
    left: Math.max(0, Math.min(maxLeft, desiredLeft)),
    top: Math.max(0, Math.min(maxTop, desiredTop)),
  };
}
